/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import {
	Box,
	Button,
	Container,
	Divider,
	Flex,
	ListItem,
	Text,
	UnorderedList,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverHeader,
	PopoverBody,
	PopoverArrow,
	PopoverCloseButton,
	useToast,
	useDisclosure,
	FormControl,
	Switch,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
	IconButton,
	Tooltip,
} from '@chakra-ui/react';
import { AddIcon, AttachmentIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { faEdit, faPrint, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import JoditEditor from 'jodit-react';
import ReactHtmlParser from 'react-html-parser';
import AdditionalAttachment from '../additional-attachment/additional-attachment';
import AddReference from './AddReferenceModal';
import EditReference from './EditReferenceModal';
import DownloadAndViewButton from '../../../../components/Files/DownloadAndViewButton';
import LabelDetail from '../../../../components/LabelDetail';
import AddOtherAttachmentModal from './AddOtherAttachmentModal';
import RichTextEditorViewer from '../../../../components/form/RichTextEditorField/viewer';
import useStore from '../../../../store';
import jodit from '../../../../utils/jodit';
import c from '../../../../constant';

const SAVE_EDIT_NP = gql`
	mutation saveEditNp($id: String!, $subject: String!, $description: String!, $references: JSON, $attachments: JSON, $attachings: JSON) {
		saveEditNp(id: $id, subject: $subject, description: $description, references: $references, attachments: $attachments, attachings: $attachings) {
			id
		}
	}
`;

const M_OTHER_ATTACHMENTS = gql`
	mutation allowAddingOtherAttachments($id: String!, $status: Boolean!) {
		allowAddingOtherAttachments(id: $id, status: $status) {
			id
			createdBy
			createdAt
			step
			number
			subject
			references
			description
			attachments
			attachings
			proposalNumber
			doneFile
			allowAddingOtherAttachments
			type
			office
			department
			completedAt
			urgent
			createdByUser {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			viewers {
				id
				createdAt
				createdBy
				userViewer {
					id
					username
					fullName
					fullNameKhmer
					avatar
					department
					office
				}
			}
			timelines {
				id
				createdAt
				user {
					id
					username
					fullName
					fullNameKhmer
				}
				action
				description
			}
			proposal {
				id
				no
			}
			checkInAt
			checkOutAt
			checkInDdgAt
			checkInDgAt
			assistantColor
			isDepartmentLevel
			relation {
				id
				np {
					id
					number
					subject
					step
				}
				offices
				departments
				relations {
					id
					number
					subject
					step
				}
				createdAt
				updatedAt
				deleted
			}
			taggings {
				id
				npId
				tag {
					id
					createdAt
					createdBy
					name
				}
			}
		}
	}
`;

const mappingStep = { draft: 1, office: 2, department: 3, ddg: 4, assistant: 5, dg: 6, fsa: 7, done: 8 };

function NPEditor({ detail = null, onChange = () => null }) {
	const toast = useToast();
	const referenceModal = useDisclosure();
	const attachingModal = useDisclosure();
	const otherModal = useDisclosure();
	const cancelEditingModal = useDisclosure();
	const { t } = useTranslation();
	const { currentUser } = useStore((state) => state?.currentUser);
	const [isEditing, setIsEditing] = useState(false);
	const [subject, setSubject] = useState('');
	const [references, setReferences] = useState([]);
	const [description, setDescription] = useState('');
	const [removingReferenceId, setRemovingReferenceId] = useState(null);
	const [removingAttachingId, setRemovingAttachingId] = useState(null);
	const [saveEditNP, { data: dataSaveEditNp, loading: loadingSaveEditNp, error: saveEditError }] = useMutation(SAVE_EDIT_NP);
	const [additionalAttachmentData, setAdditionalAttachmentData] = useState(null);
	const [attachings, setAttachings] = useState([]);
	const [editReference, setEditReference] = useState(null);
	const [editAttaching, setEditAttaching] = useState(null);
	const [canAddingOtherAttachments, setCanAddingOtherAttachments] = useState(false);
	const [allowAddingOtherAttachments, { loading: loadingAllowAddingOtherAttachments }] = useMutation(M_OTHER_ATTACHMENTS);

	const canEdit = useMemo(() => {
		const step = mappingStep[detail?.step];
		const position = currentUser?.position;
		const role = currentUser?.role || '';
		const roles = role?.split(',');
		if (roles?.includes('assistant')) {
			return (
				[5, 7, 8]?.includes(step) ||
				(step === 1 && currentUser?.id === detail?.createdBy) ||
				(detail?.office === currentUser?.office && detail?.department === currentUser?.department)
			);
		} else {
			if (step > 7) {
				return false;
			} else {
				if (!roles?.includes('admin')) {
					if (/Hor Sovathana/.test(currentUser?.fullName) && /ហោ សុវឌ្ឍនា/.test(currentUser?.fullNameKhmer)) {
						return step === 4 || (step === 1 && currentUser?.id === detail?.createdBy);
					} else {
						if (['7', '8']?.includes(position)) {
							return step === 1;
						} else if (['6', '5']?.includes(position)) {
							return step === 2 || (step === 1 && currentUser?.id === detail?.createdBy);
						} else if (['4', '3']?.includes(position)) {
							return step === 3 || (step === 1 && currentUser?.id === detail?.createdBy);
						} else if (['2']?.includes(position)) {
							return step === 4 || (step === 1 && currentUser?.id === detail?.createdBy);
						} else if (['1']?.includes(position)) {
							return step === 6 || (step === 1 && currentUser?.id === detail?.createdBy);
						} else {
							return false;
						}
					}
				} else {
					return true;
				}
			}
		}
	}, [detail]);

	useEffect(() => {
		if (detail) {
			setSubject(detail?.subject || '');
			setReferences(detail?.references || []);
			setDescription(detail?.description || '');
			setAdditionalAttachmentData(detail?.attachments || []);
			setAttachings(detail?.attachings || []);
			setCanAddingOtherAttachments(detail?.allowAddingOtherAttachments);
		}
	}, [detail]);

	useEffect(() => {
		if (dataSaveEditNp?.saveEditNp) {
			setIsEditing(false);
			onChange();
		}
	}, [dataSaveEditNp]);

	useEffect(() => {
		if (saveEditError) {
			if (saveEditError?.graphQLErrors?.length > 0) {
				saveEditError?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(saveEditError?.message ? saveEditError?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [saveEditError]);

	function saveEdit(attachments, attachings, id, subject, references, description) {
		saveEditNP({
			variables: {
				id,
				subject,
				references,
				description,
				attachments,
				attachings,
			},
		});
	}

	return (
		<>
			<Flex justifyContent={{ base: 'center', sm: 'center', md: 'flex-end' }}>
				<Button
					mr="2"
					onClick={() => window?.print()}
					leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
					colorScheme="gray"
					variant="solid"
					size="sm"
					borderRadius={c.borderRadius}
					border="1px solid #bdc3c7"
					w="100px"
				>
					{t('Print')}
				</Button>
				{isEditing ? (
					<>
						<Button
							mr="2"
							leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
							colorScheme="red"
							variant="solid"
							size="sm"
							borderRadius={c.borderRadius}
							border="1px solid #bdc3c7"
							w="100px"
							onClick={cancelEditingModal?.onOpen}
						>
							{t('Cancel')}
						</Button>
						<Button
							isLoading={loadingSaveEditNp}
							mr="2"
							leftIcon={<FontAwesomeIcon icon={faSave} style={{ fontSize: 16 }} />}
							colorScheme="green"
							variant="solid"
							size="sm"
							borderRadius={c.borderRadius}
							border="1px solid #bdc3c7"
							w="100px"
							onClick={() => saveEdit(additionalAttachmentData, attachings, detail?.id, subject, references, description)}
						>
							{t('Save')}
						</Button>
					</>
				) : (
					<Button
						mr="2"
						leftIcon={<FontAwesomeIcon icon={faEdit} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
						onClick={() => setIsEditing(true)}
						isDisabled={canEdit ? false : true}
						w="100px"
					>
						{t('Edit')}
					</Button>
				)}
			</Flex>
			<Divider mt="4" />
			{isEditing ? (
				<>
					<Box mt="4">
						<Box key={10000} width="100%" mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Subject')}
							</Text>
							<div key={10001}>
								<JoditEditor value={subject} config={editorConfigSubject} onChange={setSubject} />
							</div>
						</Box>
						<Box key={20000} width="100%" mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Reference')}
							</Text>
							{references?.map((item, i) => (
								<Flex flex={1} key={i} justifyContent="flex-start" alignItems="center">
									<UnorderedList width="100%" onClick={() => setEditReference(item)}>
										<ListItem cursor="pointer" _hover={{ bg: 'gray.100' }} textAlign={'justify'}>
											<Flex flex={1}>
												<Flex flex={1}>
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<Text fontSize="md" fontWeight="normal">
															{ReactHtmlParser(item?.description)}
														</Text>
													</Flex>
													<Flex justifyContent="flex-end" minWidth={12}>
														{(item?.file?.url || item?.file?.id) && <AttachmentIcon color="blue.500" mt={1} />}
													</Flex>
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
									<Tooltip label={t('Edit')}>
										<IconButton
											size="sm"
											colorScheme="blue"
											icon={<EditIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setEditReference(item)}
											ml={4}
											my={1}
										/>
									</Tooltip>
									<Tooltip label={t('Remove')}>
										<IconButton
											size="sm"
											colorScheme="red"
											icon={<DeleteIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setRemovingReferenceId(item?.id)}
											ml={2}
											my={1}
										/>
									</Tooltip>
								</Flex>
							))}
							<Tooltip label={t('Add')}>
								<IconButton
									size="sm"
									colorScheme="blue"
									icon={<AddIcon />}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
									onMouseDown={referenceModal?.onOpen}
								/>
							</Tooltip>
						</Box>
						<Box key={30000} width="100%" mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Attachments')}
							</Text>
							{attachings?.map((item, i) => (
								<Flex flex={1} key={i} justifyContent="flex-start" alignItems="center">
									<UnorderedList width="100%" onClick={() => setEditAttaching(item)}>
										<ListItem cursor="pointer" _hover={{ bg: 'gray.100' }} textAlign={'justify'}>
											<Flex flex={1}>
												<Flex flex={1}>
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<Text fontSize="md" fontWeight="normal">
															{ReactHtmlParser(item?.description)}
														</Text>
													</Flex>
													<Flex justifyContent="flex-end" minWidth={12}>
														{(item?.file?.url || item?.file?.id) && <AttachmentIcon color="blue.500" mt={1} />}
													</Flex>
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
									<Tooltip label={t('Edit')}>
										<IconButton
											size="sm"
											colorScheme="blue"
											icon={<EditIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setEditAttaching(item)}
											ml={4}
											my={1}
										/>
									</Tooltip>
									<Tooltip label={t('Remove')}>
										<IconButton
											size="sm"
											colorScheme="red"
											icon={<DeleteIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setRemovingAttachingId(item?.id)}
											ml={2}
											my={1}
										/>
									</Tooltip>
								</Flex>
							))}
							<Tooltip label={t('Add')}>
								<IconButton
									size="sm"
									colorScheme="blue"
									icon={<AddIcon />}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
									onMouseDown={attachingModal?.onOpen}
								/>
							</Tooltip>
						</Box>
						<Divider />
						<Box key={40000} width="100%" mb={8} mt={4}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Description')}
							</Text>
							<div key={40001}>
								<JoditEditor value={description} config={editorConfigDetail} onBlur={setDescription} />
							</div>
						</Box>
						<Divider />
						<Box key={50000} width="60%" mb={8}>
							<AdditionalAttachment
								isEditing={isEditing}
								initData={additionalAttachmentData}
								onChange={(e) => {
									setAdditionalAttachmentData(e);
								}}
							/>
						</Box>
					</Box>
				</>
			) : (
				<Container maxW="container.xl" mt="16px" mb="16px">
					<Box mt="16px">
						<Box w="100%" overflow="hidden" _hover={{ bg: 'gray.100' }} mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Subject')}
							</Text>
							<Box w="100%">
								<RichTextEditorViewer value={detail?.subject} />
							</Box>
						</Box>
						{references?.length > 0 && (
							<Box w="100%" mb={8}>
								<Text fontSize="lg" mb="2" fontWeight="bold">
									{t('Reference')}
								</Text>
								{references?.map((item, i) => (
									<UnorderedList key={i}>
										<ListItem _hover={{ bg: 'gray.100' }}>
											<Flex flex={1} justifyContent="flex-start" alignItems="center">
												<Flex flex={1} mr={4} justifyContent="flex-start">
													<RichTextEditorViewer value={item?.description} />
												</Flex>
												<Flex justifyContent="flex-end" minWidth={12}>
													{item?.file?.url && (
														<Popover>
															<PopoverTrigger>
																<AttachmentIcon cursor="pointer" color="blue.500" mt={1} />
															</PopoverTrigger>
															<PopoverContent>
																<PopoverArrow />
																<PopoverCloseButton />
																<PopoverHeader>{t('Attachment')}</PopoverHeader>
																<PopoverBody>
																	<Flex justify={'center'}>
																		<DownloadAndViewButton url={item?.file?.url} size={item?.file?.size} />
																	</Flex>
																</PopoverBody>
															</PopoverContent>
														</Popover>
													)}
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
								))}
							</Box>
						)}
						{attachings?.length > 0 && (
							<Box w="100%" mb={8}>
								<Text fontSize="lg" mb="2" fontWeight="bold">
									{t('Attachments')}
								</Text>
								{attachings.map((item, i) => (
									<UnorderedList key={i}>
										<ListItem _hover={{ bg: 'gray.100' }}>
											<Flex flex={1} justifyContent="flex-start" alignItems="center">
												<Flex flex={1} mr={4} justifyContent="flex-start">
													<RichTextEditorViewer value={item?.description} />
												</Flex>
												<Flex justifyContent="flex-end" minWidth={12}>
													{item?.file?.url && (
														<Popover>
															<PopoverTrigger>
																<AttachmentIcon cursor="pointer" color="blue.500" mt={1} />
															</PopoverTrigger>
															<PopoverContent>
																<PopoverArrow />
																<PopoverCloseButton />
																<PopoverHeader>{t('Attachment')}</PopoverHeader>
																<PopoverBody>
																	<Flex justify={'center'}>
																		<DownloadAndViewButton url={item?.file?.url} size={item?.file?.size} />
																	</Flex>
																</PopoverBody>
															</PopoverContent>
														</Popover>
													)}
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
								))}
							</Box>
						)}
						{detail?.description && (
							<>
								<Divider />
								<Box w="100%" mb={8} mt={4}>
									<RichTextEditorViewer value={detail?.description} />
								</Box>
								<Divider />
							</>
						)}
						<Box width="60%">
							<AdditionalAttachment
								isEditing={isEditing}
								initData={additionalAttachmentData}
								onChange={(e) => {
									setAdditionalAttachmentData(e);
								}}
							/>
						</Box>
						{(currentUser?.role === 'admin' || currentUser?.position === '1') && (
							<Box width="100%" mt="4">
								<LabelDetail
									mb={1}
									mt={2}
									labelWidth="169px"
									direction="row"
									label="Allow adding other attachments"
									childDetail={
										<FormControl display="flex" justifyContent="flex-start" alignItems="center" mt={2}>
											<Switch
												isReadOnly={loadingAllowAddingOtherAttachments}
												isChecked={canAddingOtherAttachments}
												id="other-attachments"
												colorScheme="blue"
												size="md"
												onChange={(e) => {
													const id = detail?.id;
													const status = canAddingOtherAttachments ? false : true;
													const variables = { id, status };
													setCanAddingOtherAttachments(status);
													allowAddingOtherAttachments({ variables })
														.then(({ data }) => {
															onChange();
														})
														.catch((error) => {
															if (error?.graphQLErrors?.length > 0) {
																error?.graphQLErrors.map(({ message }, i) =>
																	toast({
																		title: t(message),
																		status: 'error',
																		isClosable: true,
																		position: 'bottom-right',
																	})
																);
															} else {
																toast({
																	title: t(error?.message ? error?.message : 'something went wrong'),
																	status: 'error',
																	isClosable: true,
																	position: 'bottom-right',
																});
															}
														});
												}}
											/>
										</FormControl>
									}
								/>
							</Box>
						)}
						{canAddingOtherAttachments && (
							<Box width="100%" mt="2">
								<Button onMouseDown={otherModal?.onOpen} colorScheme="blue" variant="outline" size="sm">
									{t('Add other attachments')}
								</Button>
							</Box>
						)}
					</Box>
				</Container>
			)}
			{referenceModal?.isOpen && (
				<AddReference
					title="Reference"
					list={references}
					onClose={referenceModal?.onClose}
					onApply={(list) => {
						setReferences(list);
						referenceModal?.onClose();
					}}
				/>
			)}
			{editReference && (
				<EditReference
					title="Reference"
					item={editReference}
					onClose={() => setEditReference(null)}
					onApply={(reference) => {
						setReferences((prev) => prev.map((ref) => (ref?.id === reference?.id ? reference : ref)));
						setEditReference(null);
					}}
				/>
			)}
			{attachingModal?.isOpen && (
				<AddReference
					title="Attachments"
					list={attachings}
					onClose={attachingModal?.onClose}
					onApply={(list) => {
						setAttachings(list);
						attachingModal?.onClose();
					}}
				/>
			)}
			{editAttaching && (
				<EditReference
					title="Attachments"
					item={editAttaching}
					onClose={() => setEditAttaching(null)}
					onApply={(attaching) => {
						setAttachings((prev) => prev.map((att) => (att?.id === attaching?.id ? attaching : att)));
						setEditAttaching(null);
					}}
				/>
			)}
			{otherModal?.isOpen && (
				<AddOtherAttachmentModal npId={detail?.id} initData={detail?.attachments || []} onClose={otherModal?.onClose} onCallback={onChange} />
			)}
			<AlertDialog isOpen={cancelEditingModal?.isOpen} onClose={cancelEditingModal?.onClose}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Cancel')}
						</AlertDialogHeader>
						<AlertDialogBody>
							{detail?.type === 'np' && t('Are you sure to cancel editing of note of presentation?')}
							{detail?.type === 'rp' && t('Are you sure to cancel editing of report?')}
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={cancelEditingModal?.onClose}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									cancelEditingModal?.onClose();
									setIsEditing(false);
									onChange();
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
			<AlertDialog isOpen={removingReferenceId ? true : false} onClose={() => setRemovingReferenceId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure to remove reference?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={() => setRemovingReferenceId(null)}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									setRemovingReferenceId(null);
									setReferences((prev) => prev.filter((ref) => ref?.id !== removingReferenceId));
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
			<AlertDialog isOpen={removingAttachingId ? true : false} onClose={() => setRemovingAttachingId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure to remove attachment?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={() => setRemovingAttachingId(null)}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									setRemovingAttachingId(null);
									setAttachings((prev) => prev.filter((att) => att?.id !== removingAttachingId));
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}

export default NPEditor;

const editorConfigSubject = {
	minHeight: 100,
	showPlaceholder: false,
	toolbarSticky: false,
	toolbarAdaptive: false,
	toolbarInlineForSelection: false,
	inline: false,
	readonly: false,
	toolbar: true,
	statusbar: false,
	buttons: jodit?.buttons,
	controls: jodit?.controls,
};

const editorConfigDetail = {
	minHeight: 250,
	showPlaceholder: false,
	toolbarSticky: false,
	toolbarAdaptive: false,
	toolbarInlineForSelection: false,
	inline: false,
	readonly: false,
	toolbar: true,
	statusbar: false,
	buttons: jodit?.buttons,
	controls: jodit?.controls,
};
